/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { MyDay, Task } from "../shared/types";
import { localDate, shiftDate } from "../shared/dates";
import { useStaff } from "../shared/useStaff";
import { ProcedureRunner } from "../Procedures/ProcedureRunner";
import { PrepRunner } from "../Prep/PrepRunner";
import { QuickAddTask } from "../Tasks/QuickAddTask";
import { TaskEditor } from "../Tasks/TaskEditor";
import { TaskRow } from "../Tasks/TaskRow";
import { DueCard } from "./DueCard";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

// MyDay: everything due today in one place — procedures, prep and tasks —
// and how far along each is. See docs/plans/workday/readme.md.
export const WorkDayMyDayPage = defineComponent({
  name: "WorkDayMyDayPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const router = useRouter();
    const staff = useStaff();

    const date = ref(localDate());
    const day = ref<MyDay | null>(null);
    const loading = ref(false);
    const runningProcedure = ref<number | null>(null);
    const runningPrep = ref<number | null>(null);
    const editingTask = ref<Task | null>(null);

    const load = async () => {
      loading.value = true;
      try {
        day.value = (await trpc.workday.myDay.query({
          date: date.value,
        })) as MyDay;
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };
    onMounted(load);
    watch(date, load);

    const isToday = computed(() => date.value === localDate());

    const summary = computed(() => {
      const d = day.value;
      if (!d) return { done: 0, total: 0 };
      const items = [
        ...d.procedures.map((p) => p.status === "completed"),
        ...d.prep_lists.map((l) => l.completed_at != null),
        ...d.tasks.map((x) => x.status === "done"),
      ];
      return { done: items.filter(Boolean).length, total: items.length };
    });

    const toggleTask = async (task: Task) => {
      try {
        await trpc.workday.tasks.update.mutate({
          id: task.id,
          status: task.status === "done" ? "open" : "done",
        });
        await load();
      } catch {
        toast.showToast(t("workday.common.save_failed"), { type: "error" });
      }
    };

    const greeting = () => {
      const hour = new Date().getHours();
      return t(
        hour < 12
          ? "workday.myday.morning"
          : hour < 18
            ? "workday.myday.afternoon"
            : "workday.myday.evening",
      );
    };

    const section = (
      title: string,
      icon: string,
      count: number,
      body: unknown,
      action?: unknown,
    ) => (
      <section class="space-y-3">
        <div class="flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-base-content/60">
            <span class="material-symbols-outlined text-base">{icon}</span>
            {title}
            <span class="badge badge-ghost badge-sm">{count}</span>
          </h2>
          {action}
        </div>
        {body}
      </section>
    );

    const ring = () => {
      const { done, total } = summary.value;
      const pct = total ? Math.round((done / total) * 100) : 0;
      return (
        <div
          class="radial-progress text-primary"
          style={`--value:${pct};--size:4.5rem;--thickness:6px`}
          role="progressbar"
          aria-valuenow={pct}
        >
          <span class="text-sm font-semibold text-base-content">{`${done}/${total}`}</span>
        </div>
      );
    };

    return () => {
      const d = day.value;
      return (
        <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
          <SubpageHeader
            title={new Date(`${date.value}T12:00:00`).toLocaleDateString(
              undefined,
              { weekday: "long", day: "numeric", month: "long" },
            )}
            subtitle={isToday.value ? greeting() : t("workday.myday.planning")}
          >
            {d && summary.value.total > 0 && (
              <div class="flex items-center gap-3">
                {ring()}
                <div class="text-sm">
                  <div class="font-semibold">
                    {summary.value.done === summary.value.total
                      ? t("workday.myday.all_done")
                      : t("workday.myday.progress")}
                  </div>
                  <div class="text-base-content/60">
                    {t("workday.myday.left").replace(
                      "{count}",
                      String(summary.value.total - summary.value.done),
                    )}
                  </div>
                </div>
              </div>
            )}
            <div class="flex items-center gap-1">
              <button
                class="btn btn-ghost btn-sm btn-square"
                aria-label={t("workday.myday.previous")}
                onClick={() => (date.value = shiftDate(date.value, -1))}
              >
                <span class="material-symbols-outlined">chevron_left</span>
              </button>
              <button
                class="btn btn-ghost btn-sm"
                disabled={isToday.value}
                onClick={() => (date.value = localDate())}
              >
                {t("workday.tasks.today")}
              </button>
              <button
                class="btn btn-ghost btn-sm btn-square"
                aria-label={t("workday.myday.next")}
                onClick={() => (date.value = shiftDate(date.value, 1))}
              >
                <span class="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </SubpageHeader>
          <div class="flex-1 space-y-8 overflow-auto p-5">
            {!d ? (
              <div class="flex justify-center p-10">
                <Loading />
              </div>
            ) : (
              <div
                class={[
                  "space-y-8 transition-opacity",
                  loading.value ? "opacity-60" : "",
                ].join(" ")}
              >
                {section(
                  t("workday.nav.procedures"),
                  "fact_check",
                  d.procedures.length,
                  d.procedures.length === 0 ? (
                    <p class="rounded-xl bg-base-100 p-4 text-sm text-base-content/60">
                      {t("workday.myday.no_procedures")}{" "}
                      <button
                        class="link link-primary"
                        onClick={() => router.push("/app/workday/procedures")}
                      >
                        {t("workday.myday.set_up")}
                      </button>
                    </p>
                  ) : (
                    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {d.procedures.map((p) => (
                        <DueCard
                          key={p.id}
                          icon="fact_check"
                          title={p.name}
                          subtitle={t(`workday.department.${p.department}`)}
                          done={p.answered}
                          total={p.step_count}
                          complete={p.status === "completed"}
                          issues={p.issues}
                          dueTime={p.schedule_time}
                          isToday={isToday.value}
                          onOpen={() => (runningProcedure.value = p.id)}
                        />
                      ))}
                    </div>
                  ),
                )}
                {section(
                  t("workday.nav.prep"),
                  "skillet",
                  d.prep_lists.length,
                  d.prep_lists.length === 0 ? (
                    <p class="rounded-xl bg-base-100 p-4 text-sm text-base-content/60">
                      {t("workday.myday.no_prep")}{" "}
                      <button
                        class="link link-primary"
                        onClick={() => router.push("/app/workday/prep")}
                      >
                        {t("workday.myday.set_up")}
                      </button>
                    </p>
                  ) : (
                    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {d.prep_lists.map((l) => (
                        <DueCard
                          key={l.id}
                          icon="skillet"
                          title={l.name}
                          subtitle={t(`workday.department.${l.department}`)}
                          done={l.done_count}
                          total={l.item_count}
                          complete={l.completed_at != null}
                          isToday={isToday.value}
                          onOpen={() => (runningPrep.value = l.id)}
                        />
                      ))}
                    </div>
                  ),
                )}
                {section(
                  t("workday.nav.tasks"),
                  "task_alt",
                  d.tasks.length,
                  <div class="space-y-2">
                    <QuickAddTask
                      staff={staff.value}
                      defaultDue={date.value}
                      onAdded={load}
                    />
                    {d.tasks.length > 0 && (
                      <div class="rounded-xl border border-base-300 bg-base-100 p-2">
                        {d.tasks.map((task) => (
                          <TaskRow
                            key={task.id}
                            task={task}
                            staff={staff.value}
                            onToggle={() => toggleTask(task)}
                            onOpen={() => (editingTask.value = task)}
                          />
                        ))}
                      </div>
                    )}
                  </div>,
                )}
              </div>
            )}
          </div>

          {runningProcedure.value != null && (
            <ProcedureRunner
              procedureId={runningProcedure.value}
              date={date.value}
              onClose={async () => {
                runningProcedure.value = null;
                await load();
              }}
            />
          )}
          {runningPrep.value != null && (
            <PrepRunner
              listId={runningPrep.value}
              date={date.value}
              onClose={async () => {
                runningPrep.value = null;
                await load();
              }}
            />
          )}
          {editingTask.value && (
            <TaskEditor
              task={editingTask.value}
              staff={staff.value}
              onClose={async (changed: boolean) => {
                editingTask.value = null;
                if (changed) await load();
              }}
            />
          )}
        </div>
      );
    };
  },
});
